import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { exerciseLibrary } from '../../services/api';
import ExerciseEditor from './ExerciseEditor';
import MediaImportDialog from './MediaImportDialog';
import OpenMediaImportDialog from './OpenMediaImportDialog';
import OpenVideoImportDialog from './OpenVideoImportDialog';
import { Chip, SourceChips, Thumb, errorList, labelOf, useDebounced } from './shared';

/*
 * O separador "Exercícios": pesquisa com filtros, lista paginada e o editor. A pesquisa é do servidor
 * (sem acentos, todos os termos têm de aparecer); os filtros são os da API — grupo, nível, mecânica,
 * padrão de movimento, máquina, origem, só o que o ginásio consegue fazer, com/sem imagem, desativados.
 */

const PAGE_SIZE = 24;

const INITIAL_FILTERS = {
  q: '', group: '', level: '', mechanic: '', pattern: '', type: '', demo: '', equipmentId: '', source: '',
  onlyAvailable: false, hasMedia: '', assets: '', includeInactive: false, sort: 'name',
};

const ASSET_LABEL = { complete: 'Imagem e demonstração', image: 'Só imagem', demo: 'Só demonstração', none: 'Sem ativos' };

const toParams = (f, page) => {
  const p = { page, pageSize: PAGE_SIZE, sort: f.sort };
  if (f.q.trim()) p.q = f.q.trim();
  if (f.group) p.group = f.group;
  if (f.level) p.level = f.level;
  if (f.mechanic) p.mechanic = f.mechanic;
  if (f.pattern) p.pattern = f.pattern;
  if (f.type) p.type = f.type;
  if (f.demo) p.demo = f.demo;
  if (f.equipmentId) p.equipmentId = f.equipmentId;
  if (f.source) p.source = f.source;
  if (f.onlyAvailable) p.onlyAvailable = true;
  if (f.hasMedia) p.hasMedia = f.hasMedia === 'true';
  if (f.assets) p.assets = f.assets;
  if (f.includeInactive) p.includeInactive = true;
  return p;
};

export default function ExercisesPanel({ meta, machines, onCountChange, onCatalogChanged }) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(undefined);   // undefined = fechado · null = novo · id = editar
  const [importing, setImporting] = useState(false);
  const [importingOpen, setImportingOpen] = useState(false);
  const [importingVideo, setImportingVideo] = useState(false);
  const [importedNote, setImportedNote] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);
  // Os callbacks do pai podem mudar a cada render; a pesquisa só se repete quando os filtros mudam.
  const callbacks = useRef({ onCountChange, onCatalogChanged });
  callbacks.current = { onCountChange, onCatalogChanged };

  const debouncedQuery = useDebounced(filters.q, 300);
  // A chave é o conteúdo: digitar muda `filters.q` a cada letra, mas só o texto já "assentado" conta para a pesquisa.
  const effectiveKey = JSON.stringify({ ...filters, q: debouncedQuery });
  const effective = useMemo(() => JSON.parse(effectiveKey), [effectiveKey]);

  useEffect(() => { setPage(1); }, [effective]);

  useEffect(() => {
    const request = ++requestRef.current;
    setLoading(true);
    exerciseLibrary.search(toParams(effective, page))
      .then(({ data }) => {
        if (request !== requestRef.current) return;
        setResult(data);
        setError(null);
        callbacks.current.onCountChange?.(data.total, effective);
      })
      .catch((e) => { if (request === requestRef.current) setError(errorList(e, 'Não foi possível carregar os exercícios.').join(' ')); })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
  }, [effective, page, reloadKey]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const pages = Math.max(1, Math.ceil(result.total / result.pageSize));
  const filtered = JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS);

  const refresh = useCallback(() => { setReloadKey((k) => k + 1); callbacks.current.onCatalogChanged?.(); }, []);

  return (
    <div>
      <div className="lib-filters">
        <div className="lib-filters-top">
          <input className="lib-input" type="search" value={filters.q} onChange={(e) => set({ q: e.target.value })}
            placeholder="Procurar por nome, músculo ou máquina…" aria-label="Procurar exercícios" />
          <button type="button" className="lib-btn lib-filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}>Filtros</button>
          {meta.canManage && <button type="button" className="lib-btn" onClick={() => setImporting(true)}>Importar ativos</button>}
          {meta.canManage && <button type="button" className="lib-btn" onClick={() => setImportingOpen(true)}>Demonstrações de domínio público</button>}
          {meta.canManage && <button type="button" className="lib-btn" onClick={() => setImportingVideo(true)}>Mini vídeos de licença aberta</button>}
          {meta.canManage && <button type="button" className="lib-btn" data-testid="filming-list"
            title="A lista que o instrutor leva para o ginásio: os exercícios sem vídeo aprovado, por grupo"
            onClick={() => set({ assets: 'no-demo', sort: 'group' })}>Lista de filmagem</button>}
          {meta.canManage && <button type="button" className="lib-btn primary" onClick={() => setEditing(null)}>+ Novo</button>}
        </div>

        <div className={`lib-filter-grid${filtersOpen ? ' open' : ''}`}>
          <select className="lib-select" aria-label="Grupo muscular" value={filters.group} onChange={(e) => set({ group: e.target.value })}>
            <option value="">Todos os grupos</option>
            {meta.groups.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Nível" value={filters.level} onChange={(e) => set({ level: e.target.value })}>
            <option value="">Todos os níveis</option>
            {meta.levels.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Mecânica" value={filters.mechanic} onChange={(e) => set({ mechanic: e.target.value })}>
            <option value="">Qualquer mecânica</option>
            {meta.mechanics.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Padrão de movimento" value={filters.pattern} onChange={(e) => set({ pattern: e.target.value })}>
            <option value="">Qualquer movimento</option>
            {meta.patterns.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Tipo de exercício" value={filters.type} onChange={(e) => set({ type: e.target.value })}>
            <option value="">Qualquer tipo</option>
            {(meta.exerciseTypes || []).map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Demonstração em vídeo" value={filters.demo} onChange={(e) => set({ demo: e.target.value })}>
            <option value="">Com ou sem demonstração</option>
            <option value="approved">Demonstração aprovada</option>
            {meta.canManage && <option value="pending">Demonstração pendente de validação</option>}
            {meta.canManage && <option value="rejected">Demonstração rejeitada</option>}
            <option value="none">Sem demonstração</option>
          </select>
          <select className="lib-select" aria-label="Máquina ou equipamento" value={filters.equipmentId} onChange={(e) => set({ equipmentId: e.target.value })}>
            <option value="">Qualquer máquina</option>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select className="lib-select" aria-label="Origem" value={filters.source} onChange={(e) => set({ source: e.target.value })}>
            <option value="">Catálogo e do ginásio</option>
            <option value="catalog">Só do catálogo</option>
            <option value="custom">Só do ginásio</option>
          </select>
          <select className="lib-select" aria-label="Imagem" value={filters.hasMedia} onChange={(e) => set({ hasMedia: e.target.value })}>
            <option value="">Com ou sem imagem</option>
            <option value="true">Com imagem</option>
            <option value="false">Sem imagem</option>
          </select>
          <select className="lib-select" aria-label="Estado dos ativos" value={filters.assets} onChange={(e) => set({ assets: e.target.value })}>
            <option value="">Todos os estados</option>
            <option value="complete">Com imagem e demonstração</option>
            <option value="no-image">Sem imagem</option>
            <option value="no-demo">Sem demonstração</option>
            {meta.canManage && <option value="pending">Com ficheiros pendentes de aprovação</option>}
            {meta.canManage && <option value="rejected">Com ficheiros rejeitados</option>}
          </select>
          <select className="lib-select" aria-label="Ordenar" value={filters.sort} onChange={(e) => set({ sort: e.target.value })}>
            <option value="name">Ordenar por nome</option>
            <option value="group">Ordenar por grupo</option>
            <option value="recent">Mais recentes</option>
          </select>
          <div className="lib-filter-checks">
            <label className="lib-check"><input type="checkbox" checked={filters.onlyAvailable} onChange={(e) => set({ onlyAvailable: e.target.checked })} />Só o que o ginásio consegue fazer</label>
            {meta.canManage && <label className="lib-check"><input type="checkbox" checked={filters.includeInactive} onChange={(e) => set({ includeInactive: e.target.checked })} />Incluir desativados</label>}
            {filtered && <button type="button" className="lib-btn small ghost" onClick={() => setFilters(INITIAL_FILTERS)}>Limpar filtros</button>}
          </div>
        </div>
      </div>

      {error && <div className="lib-notice bad" role="alert">{error}</div>}
      {importedNote && <div className="lib-notice" role="status">{importedNote}</div>}

      {/* A vista de trabalho do instrutor: sem demonstração aprovada, ordenada por grupo. */}
      {filters.assets === 'no-demo' && filters.sort === 'group' && (
        <div className="lib-banner" role="status" data-testid="filming-list-banner">
          <strong>Lista de filmagem</strong>
          <span>{result.total} {result.total === 1 ? 'exercício' : 'exercícios'} sem demonstração aprovada, por grupo — o que ainda falta filmar. Toque num exercício para abri-lo e enviar o vídeo.</span>
          <button type="button" className="lib-btn small" onClick={() => setFilters(INITIAL_FILTERS)}>Sair da lista</button>
        </div>
      )}

      <div aria-live="polite">
        {loading && result.items.length === 0 && <div className="lib-loading" role="status">A carregar…</div>}
        {!loading && !error && result.items.length === 0 && (
          <div className="lib-empty">
            {filtered ? 'Nenhum exercício com estes filtros.' : 'A biblioteca ainda não tem exercícios.'}
            {filtered && <div style={{ marginTop: 8 }}><button type="button" className="lib-btn small" onClick={() => setFilters(INITIAL_FILTERS)}>Limpar filtros</button></div>}
          </div>
        )}
      </div>

      <div className="lib-list" style={{ opacity: loading && result.items.length > 0 ? 0.6 : 1 }}>
        {result.items.map((item) => (
          <button key={item.id} type="button" className={`lib-card${item.isActive ? '' : ' inactive'}`} onClick={() => setEditing(item.id)}
            aria-label={`${item.name}${meta.canManage ? ' — abrir' : ''}`}>
            <Thumb src={item.thumbUrl || item.imageUrl} alt="" />
            <span className="lib-card-body">
              <span className="lib-card-title">{item.name}</span>
              {item.nameEn && <span className="lib-card-sub">{item.nameEn}</span>}
              <span className="lib-chips">
                {item.groupLabel && <Chip tone="accent">{item.groupLabel}</Chip>}
                {item.levelLabel && <Chip>{item.levelLabel}</Chip>}
                {item.mechanic && <Chip>{labelOf(meta.mechanics, item.mechanic)}</Chip>}
                {item.exerciseType && item.exerciseType !== 'strength' && <Chip>{item.exerciseTypeLabel || labelOf(meta.exerciseTypes || [], item.exerciseType)}</Chip>}
                {item.demoStatus && item.demoStatus !== 'none' && <Chip tone={item.demoStatus === 'approved' ? 'accent' : 'warn'} title="Estado da demonstração em vídeo">{item.demoStatusLabel}</Chip>}
                <SourceChips item={item} />
                {!item.available && <Chip tone="warn" title="Falta, neste ginásio, uma máquina de que o exercício precisa">Sem máquina</Chip>}
                {!item.hasMedia && <Chip title="Ainda sem imagem">Sem imagem</Chip>}
                {meta.canManage && item.assetState && item.assetState !== 'none' && item.assetState !== 'complete' && (
                  <Chip title="Que ativos aprovados o exercício tem">{ASSET_LABEL[item.assetState]}</Chip>
                )}
                {meta.canManage && item.pendingAssets > 0 && (
                  <Chip tone="warn" title="Ficheiros à espera de aprovação">{item.pendingAssets} por aprovar</Chip>
                )}
              </span>
              <span className="lib-card-meta">
                {item.equipmentLinks.length > 0
                  ? item.equipmentLinks.map((e) => e.name).join(' · ')
                  : 'Sem equipamento'}
              </span>
            </span>
          </button>
        ))}
      </div>

      {result.total > 0 && (
        <div className="lib-pager">
          <span>{result.total} {result.total === 1 ? 'exercício' : 'exercícios'} · página {result.page} de {pages}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="lib-btn small" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <button type="button" className="lib-btn small" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>Seguinte</button>
          </span>
        </div>
      )}

      {importing && (
        <MediaImportDialog
          onClose={() => setImporting(false)}
          onImported={(report) => {
            setImportedNote(`Importação concluída: ${report.added} acrescentados, ${report.skipped} já existiam, ${report.errors} com erro.`);
            refresh();
          }}
        />
      )}

      {importingOpen && (
        <OpenMediaImportDialog
          onClose={() => setImportingOpen(false)}
          onImported={(data) => {
            setImportedNote(`Demonstrações de domínio público: ${data.added} exercícios por validar, ${data.skipped} ignorados, ${data.errors} com erro. Valide as imagens no exercício, na secção «Imagens».`);
            refresh();
          }}
        />
      )}

      {importingVideo && (
        <OpenVideoImportDialog
          onClose={() => setImportingVideo(false)}
          onImported={(data) => {
            setImportedNote(`Mini vídeos de licença aberta: ${data.added} exercícios por validar, ${data.skipped} ignorados, ${data.errors} com erro. Confirme o movimento no exercício, na secção «Imagens» (o ficheiro está marcado «Vídeo»), antes de aprovar.`);
            refresh();
          }}
        />
      )}

      {editing !== undefined && (
        <ExerciseEditor
          key={editing ?? 'new'}
          exerciseId={editing}
          meta={meta}
          machines={machines}
          onClose={() => { setEditing(undefined); refresh(); }}
          onSaved={() => setReloadKey((k) => k + 1)}
          onDeleted={() => { setEditing(undefined); refresh(); }}
        />
      )}
    </div>
  );
}
